import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { FlexiRow, Select } from '../../../../styles/styled';
import Colors from '../../../../styles/colors';

class Mapper extends Component {
  constructor(props) {
    super(props);

    const matches = !isEmpty(Object.keys(props.oldMatches))
      ? this.getOldMatches()
      : this.getAutoMatches();

    this.state = {
      showAddNewButton: false,
      matches: matches,
    };

    this.pleaseSelectOption = [{ text: 'Please Select ...', key: false }];
  }

  componentDidMount() {
    // if empty add at least 1 fields
    if (isEmpty(this.state.matches)) {
      this.handleAddNewMatch();
    }

    this.sendStateToParent();
  }

  componentDidUpdate() {
    this.sendStateToParent();
  }

  getOldMatches() {
    const selectedMatches = [];
    const { targetFields, sourceFields, oldMatches } = this.props;
    for (const oldMatchKey in oldMatches) {
      if (oldMatchKey) {
        const oldMatchValue = String(oldMatches[oldMatchKey]);
        targetFields.forEach((target, i) => {
          if (isEqual(target.key, oldMatchKey)) {
            let found = false;
            sourceFields.forEach((source, j) => {
              if (source.key === oldMatchValue) {
                selectedMatches.push({
                  sourceFieldIndex: j,
                  targetFieldIndex: i,
                  type: target.required === true ? 'required' : 'normal',
                });

                found = true;
              }
            });

            // insert as unmatched required field
            if (found === false) {
              selectedMatches.push({
                sourceFieldIndex: false,
                targetFieldIndex: i,
                type: target.required === true ? 'required' : 'normal',
              });
            }
          }
        });
      }
    }

    selectedMatches.sort(a => (a.type === 'required' ? -1 : 1));

    return selectedMatches;
  }

  // automatch fields
  getAutoMatches() {
    const { sourceFields, targetFields } = this.props;
    const autoMatches = [];
    targetFields.forEach((target, i) => {
      if (target.required === true) {
        let found = false;
        sourceFields.forEach((source, j) => {
          if (source.key === target.default) {
            autoMatches.push({
              sourceFieldIndex: j,
              targetFieldIndex: i,
              type: 'required',
            });

            found = true;
          }
        });

        // insert as unmatched required field
        if (found === false) {
          autoMatches.push({
            sourceFieldIndex: false,
            targetFieldIndex: i,
            type: 'required',
          });
        }
      }
    });

    return autoMatches;
  }

  filterUsedTargetFields(targetFields) {
    const { matches } = this.state;
    return targetFields.filter(field => {
      let used = false;
      matches.forEach(match => {
        const targetField = this.props.targetFields[match.targetFieldIndex];
        if (match.targetFieldIndex !== false && targetField.key === field.key) {
          used = true;
        }
      });

      return !used;
    });
  }

  generateKeyFromMatch(match) {
    return [match.sourceFieldIndex, match.targetFieldIndex, match.active]
      .map(el => (typeof el === 'string' || !el ? el : el.toString()))
      .join('_');
  }

  matchesContainsNewMatch() {
    const { matches } = this.state;
    const result = matches.filter(match => match.type === 'new');
    return !isEmpty(result);
  }

  matchesContainsFalseSourceField() {
    const { matches } = this.state;
    const result = matches.filter(match => match.sourceFieldIndex === false);
    return !isEmpty(result);
  }

  isCompleted() {
    const { matches } = this.state;
    const result = matches.filter(
      match => match.sourceFieldIndex !== false && match.targetFieldIndex !== false
    );

    return isEqual(result.length, matches.length);
  }

  isThereAvailableTargetFields() {
    const { targetFields } = this.props;
    const result = this.filterUsedTargetFields(targetFields);
    return !isEmpty(result);
  }

  isAddButtonDisabled() {
    return [
      this.matchesContainsNewMatch(),
      this.matchesContainsFalseSourceField(),
      !this.isThereAvailableTargetFields(),
      this.props.disabled,
    ].some(value => value);
  }

  sendStateToParent() {
    const matches = this.generateMatchesForParent();
    this.props.onMapperStateChange({
      matches,
      isCompleted: !isEmpty(matches) && this.isCompleted(),
    });
  }

  // produce a target:source pair
  generateMatchesForParent() {
    const { targetFields, sourceFields } = this.props;
    const oldMatchesCleaned = {};
    const { matches } = this.state;
    matches.forEach((match, index) => {
      const { targetFieldIndex, sourceFieldIndex } = match;
      if (sourceFieldIndex !== false && targetFieldIndex !== false) {
        const correspondingTargetField = targetFields[targetFieldIndex];
        const correspondingSourceField = sourceFields[sourceFieldIndex];
        const finalKey = correspondingTargetField.key;
        const finalValue = correspondingSourceField.key;
        oldMatchesCleaned[finalKey] = finalValue;
      }
    });

    return oldMatchesCleaned;
  }

  handleAddNewMatch = () => {
    const { matches = {} } = this.state;
    if (this.isAddButtonDisabled() && !isEmpty(matches)) {
      return false;
    }

    matches.push({ sourceFieldIndex: false, targetFieldIndex: false, type: 'new' });
    this.setState({
      matches,
    });
  };

  handleTargetChange = matchIndex => {
    return e => {
      const { matches } = this.state;
      const { targetFields } = this.props;
      const newKeyValue = e.target.value || '';
      const newTargetFieldIndex = targetFields.indexOf(
        targetFields.filter(el => el.key === newKeyValue)[0]
      );
      matches[matchIndex].targetFieldIndex = newTargetFieldIndex;
      matches[matchIndex].type = 'normal';
      this.setState({
        matches,
      });
    };
  };

  handleSourceChange = matchIndex => {
    return e => {
      const { matches } = this.state;
      const { sourceFields } = this.props;
      const newKeyValue = e.target.value || '';
      const currentTargetFieldIndex = matches[matchIndex].targetFieldIndex;
      const newSourceFieldIndex = sourceFields.indexOf(
        sourceFields.filter(el => el.key === newKeyValue)[0]
      );

      // verify if the newSourceFieldIndex and targetFieldIndex has already been added
      const existingData = matches.filter(match => {
        const { sourceFieldIndex, targetFieldIndex } = match;
        return (
          sourceFieldIndex === newSourceFieldIndex && targetFieldIndex === currentTargetFieldIndex
        );
      });

      if (isEmpty(existingData)) {
        matches[matchIndex].sourceFieldIndex = newSourceFieldIndex;
        this.setState({
          matches,
        });
      }
    };
  };

  handleMatchLineMouseOver(matchIndex, isOver, newType = false) {
    return e => {
      if (newType) {
        return false;
      }

      // deny mouseout handler if related target is `window`
      // happens on firefox when hovering a dropdown list
      // which loses focus to current the target
      const relatedTarget = e.relatedTarget || e.explicitOriginalTarget || document.activeElement;
      if (!isOver && relatedTarget === window) {
        return false;
      }

      // setTimeout is required to prevent reacts batching to loose data
      const { matches } = this.state;
      matches[matchIndex].active = isOver;
      setTimeout(() => {
        this.setState({
          matches,
        });
      }, 0);
    };
  }

  handleDeleteMatch = match => {
    return e => {
      const { matches } = this.state;
      const copy = [...matches];
      const deleteIndex = copy.indexOf(match);
      copy.splice(deleteIndex, 1);
      this.setState({
        matches: copy,
      });
    };
  };

  renderDeleteMatchMarkup(match) {
    return (
      <div className="col-lg-1 col-sm-1 col-xs-1 sm-no-padding">
        <ActionIcon onClick={this.handleDeleteMatch(match)}>
          <i className="title-small font-weight-700 invalid" title="Delete" />
        </ActionIcon>
      </div>
    );
  }

  renderRequiredMarkup() {
    return (
      <div className="col-lg-1 col-sm-1 col-xs-1 sm-no-padding">
        <ActionIcon>
          <i className="title-small font-weight-700 invalid" title="Required" />
        </ActionIcon>
      </div>
    );
  }

  renderCheckOkMarkup() {
    return (
      <div className="col-lg-1 col-sm-1 col-xs-1 sm-no-padding">
        <ActionIcon>
          <i className="title-small font-weight-700 valid" />
        </ActionIcon>
      </div>
    );
  }

  renderAddNewMatchButton() {
    return (
      <div className="row" key="new-button">
        <div className="col-lg-offset-4 col-md-offset-5 col-xs-offset-4 col-lg-7 col-sm-7 col-xs-7">
          <AddFieldButton
            className="btn"
            disabled={this.isAddButtonDisabled()}
            onClick={this.handleAddNewMatch}>
            + ADD NEW FIELD
          </AddFieldButton>
        </div>
      </div>
    );
  }

  generateTargetOptions(match) {
    // add please select option accordingly
    const { targetFields } = this.props;
    const pleaseSelectOption = match.targetFieldIndex === false ? this.pleaseSelectOption : [];
    return pleaseSelectOption.concat(this.filterUsedTargetFields(targetFields));
  }

  generateSourceOptions(match) {
    // add please select option accordingly
    const { sourceFields } = this.props;
    const pleaseSelectOption = match.sourceFieldIndex === false ? this.pleaseSelectOption : [];
    return pleaseSelectOption.concat(sourceFields);
  }

  renderNormalMatchLine(options = {}) {
    const { matchIndex = 0, newType = false, required = false } = options;
    const { matches } = this.state;
    const { sourceFields, targetFields } = this.props;

    const match = matches[matchIndex];
    const { active = false, targetFieldIndex, sourceFieldIndex } = match;
    const defaultSourceValue = sourceFieldIndex !== false ? sourceFields[sourceFieldIndex].key : '';
    const requiredMarkup = this.renderRequiredMarkup();
    const deleteMatchMarkup = this.renderDeleteMatchMarkup(match);
    const checkOkMarkup = this.renderCheckOkMarkup();

    let actionsMarkup = active === true ? deleteMatchMarkup : checkOkMarkup;
    if (sourceFieldIndex === false || newType) {
      actionsMarkup = deleteMatchMarkup;

      if (required) {
        actionsMarkup = requiredMarkup;
      }
    }

    const getStaticTargetText = () => {
      const { text = 'N/A' } = targetFields[targetFieldIndex];
      return <span className="text-large">{text}</span>;
    };

    return (
      <FlexiRow
        alignItems="center"
        className="row margin-one-bottom"
        key={this.generateKeyFromMatch(match)}
        onMouseEnter={this.handleMatchLineMouseOver(matchIndex, true, newType)}
        onMouseLeave={this.handleMatchLineMouseOver(matchIndex, false, newType)}>
        <div className="col-lg-4 col-sm-4 col-xs-4">
          {newType && (
            <Select className="text-large no-margin" onChange={this.handleTargetChange(matchIndex)}>
              {this.generateTargetOptions(match).map(({ key, text }, i) => {
                return (
                  <option value={key} key={`${key}-${i}`}>
                    {text}
                  </option>
                );
              })}
            </Select>
          )}
          {!newType && getStaticTargetText()}
        </div>
        <div className="col-lg-7 col-sm-7 col-xs-7">
          <Select
            className="text-large no-margin"
            value={defaultSourceValue}
            onChange={this.handleSourceChange(matchIndex)}>
            {this.generateSourceOptions(match).map(({ key, text }, i) => {
              return (
                <option value={key} key={`${key}-${i}`}>
                  {text}
                </option>
              );
            })}
          </Select>
        </div>
        {actionsMarkup}
      </FlexiRow>
    );
  }

  render() {
    const { matches } = this.state;
    const { addNewMatchButton } = this.props;
    const list = matches.map((match, matchIndex) => {
      if (match.type === 'required') {
        return this.renderNormalMatchLine({ matchIndex, required: true });
      }

      if (match.type === 'normal') {
        return this.renderNormalMatchLine({ matchIndex });
      }

      if (match.type === 'new') {
        return this.renderNormalMatchLine({ matchIndex, newType: true });
      }
    });

    if (addNewMatchButton) {
      list.push(this.renderAddNewMatchButton());
    }

    return <div>{list}</div>;
  }
}

const AddFieldButton = styled.button`
  width: 100%;
  height: 35px;
  background-color: #fff;
  color: ${Colors.mainOrange};
  border: 1px solid ${Colors.mainOrange};

  &:hover {
    background-color: ${Colors.mainOrange};
    color: #fff;
  }
`;

const ActionIcon = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    cursor: pointer;
    font-style: normal;
    &.invalid {
      &:before {
        content: '✕';
        color: #da6844;
      }
    }

    &.valid {
      &:before {
        content: '✓';
        color: #5cab40;
      }
    }
  }
`;

Mapper.propTypes = {
  sourceFields: PropTypes.array,
  targetFields: PropTypes.array,
  oldMatches: PropTypes.object,
  addNewMatchButton: PropTypes.bool,
  onMapperStateChange: PropTypes.func,
  disabled: PropTypes.bool,
};

Mapper.defaultProps = {
  sourceFields: [],
  targetFields: [],
  oldMatches: {},
  addNewMatchButton: true,
  onMapperStateChange: () => {},
  disabled: false,
};

export default Mapper;
